<template>
  <b-modal
    ref="eventManagementModal"
    size="xl"
    :title="$t('component.calendar.eventManagement')"
    centered
    modal-class="event-management-modal"
    @shown="reloadData()"
  >
    <b-container fluid>
      <b-row>
        <b-col class="d-flex justify-content-between">
          <div class="d-flex align-items-center px-2">
            <span
              v-if="eventCount"
              class="main"
            >{{ eventCount }} {{ $tc('component.calendar.event', eventCount) }}</span>
          </div>
          <div class="d-flex align-items-center">
            <b-form-checkbox v-model="onlyPendingEvents">
              <span class="dark">{{ $t('component.calendar.onlyPendingEvents') }}</span>
            </b-form-checkbox>
            <div
              style="width:1px; height:18px; background: #d0d0d0"
              class="mx-3 line-separator"
            />
            <b-button
              v-if="selectedRows.length"
              class="btn__icon btn__icon--primary  px-2"
              @click="deletePersonnelEvent(selectedRows)"
            >
              <i
                v-tooltip="$t('component.calendar.delete')"
                class="far fa-trash-alt"
                aria-hidden="true"
              />
            </b-button>
            <b-button
              v-if="selectedRows.length === 1"
              class="btn__icon btn__icon--primary  px-2"
              @click="$refs.modalEventCalendarWrapper.show([...selectedRows])"
            >
              <i
                v-tooltip="$t('component.calendar.update')"
                class="far fa-edit"
                aria-hidden="true"
              />
            </b-button>
            <b-button
              class="btn__icon btn__icon--primary  px-2"
              @click="$refs.modalEventCalendarWrapper.show()"
            >
              <i
                v-tooltip="$t('component.calendar.add')"
                class="far fa-plus"
                aria-hidden="true"
              />
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <vt-grid
            pagination
            class="event-management-grid"
            :grid-options="gridOptions"
            :column-defs="columnDefs"
          />
        </b-col>
      </b-row>
    </b-container>
    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <b-button
            :disabled="!canConfirm(selectedRows)"
            size="sm"
            class="btn--primary"
            @click="confirmUnconfirmedPersonnelEvent(selectedRows)"
          >
            <i
              class="fal fa-check mr-2"
              aria-hidden="true"
            />
            {{ $t('component.calendar.validate') }}
          </b-button>
        </div>
      </div>
    </div>
    <modal-wrapper ref="modalEventCalendarWrapper">
      <modal-event-calendar
        ref="modalEventCalendar"
        :is-manager="true"
        @createEvent="createAndConfirmPersonnelEvent($event)"
        @updateEvent="updatePersonnelEvent($event)"
        @deleteEvent="deletePersonnelEvent($event)"
      />
    </modal-wrapper>
  </b-modal>
</template>

<script>
import VtGrid from '@/components/VtGrid/VtGrid'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import i18n from '@/lang/i18n'
import ModalEventCalendar from '@/components/userConfiguration/ModalEventCalendar'
import { PersonnelEventsForEventManagersDatasource }
    from '@/api/resources/personnelEvents/personnelEventsForEventManagers/personnel-events-for-event-managers-datasource'
import PersonnelEventsForEventManagersServices
    from '@/api/resources/personnelEvents/personnelEventsForEventManagers/personnelEventsForEventManagersServices'
import { EVENT_TYPE } from '@/api/resources/personnelEvents/constants/eventType'
import { keys } from 'lodash'
import { DateUtils } from '@/shared/util/date-utils'
import { GridUtils } from '@/shared/util/grid-utils'

export default {
    name: 'EventManagementModal',
    components: {
        VtGrid,
        ModalEventCalendar
    },
    props: {
        sample: Object,
        isCaseValidated: Boolean
    },
    data() {
        return {
            onlyPendingEvents: false,
            columnDefs: [
                { type: 'customCheckbox' },
                {
                    type: 'text',
                    headerName: i18n.t('component.calendar.eventType'),
                    colId: 'type',
                    field: 'type',
                    width: 150,
                    minWidth: 140,
                    suppressSizeToFit: true,
                    filter: true,
                    filterParams: {
                        newRowsAction: 'keep',
                        suppressMiniFilter: true,
                        cellRenderer: (params) => {
                            return '<span>' + EVENT_TYPE[params.value].translatedName + '</span>'
                        },
                        values: keys(EVENT_TYPE)
                    },
                    cellRenderer: (params) => {
                        return '<span>' + params.value.translatedName + '</span>'
                    }
                },
                {
                    type: 'date',
                    headerName: i18n.t('component.calendar.startDateGrid'),
                    colId: 'startingDate',
                    field: 'startingDate',
                    width: 150,
                    minWidth: 140,
                    suppressSizeToFit: true
                },
                {
                    type: 'date',
                    headerName: i18n.t('component.calendar.endDateGrid'),
                    colId: 'endingDate',
                    field: 'endingDate',
                    width: 150,
                    minWidth: 140,
                    suppressSizeToFit: true
                },
                {
                    type: 'text',
                    headerName: i18n.t('component.calendar.eventStatus'),
                    colId: 'status',
                    field: 'confirmationDate',
                    width: 150,
                    minWidth: 140,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false,
                    cellRenderer: (params) => {
                        if (!params.value) {
                            return `<span>${i18n.tc('component.calendar.pendingToValidate')}</span>`
                        }
                        return `<span>${i18n.tc('component.calendar.validated')}</span>`
                    }
                },
                {
                    type: 'text',
                    headerName: i18n.t('component.calendar.eventUser'),
                    colId: 'personnel.normalizedFullName',
                    field: 'personnel.fullName',
                    width: 150,
                    minWidth: 140,
                    suppressSizeToFit: true
                },
                {
                    type: 'text',
                    headerName: i18n.t('global.observations'),
                    colId: 'observations',
                    field: 'observations',
                    width: 150,
                    minWidth: 140,
                    suppressSizeToFit: true
                },
                {
                    type: 'date',
                    headerName: i18n.t('component.calendar.requestDate'),
                    colId: 'creationDate',
                    field: 'creationDate',
                    width: 150,
                    minWidth: 140,
                    suppressSizeToFit: true
                },
                {
                    type: 'date',
                    headerName: i18n.t('component.calendar.validationDate'),
                    colId: 'confirmationDate',
                    field: 'confirmationDate',
                    width: 150,
                    minWidth: 140,
                    suppressSizeToFit: true
                }
            ],
            selectedRows: [],
            gridApi: null
        }
    },
    computed: {
        eventCount() {
            if (this.gridApi && this.gridApi.paginationGetTotalPages()) {
                return this.gridApi ? this.gridApi.paginationGetRowCount() : null
            }
            return null
        },
        laboratoryId() {
            return this.$store.getters.labId
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged
            }
        }
    },
    watch: {
        onlyPendingEvents() {
            this.reloadData()
        }
    },
    methods: {
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PersonnelEventsForEventManagersDatasource(this.laboratoryId))
        },
        reloadData() {
            if (this.gridApi) {
                let externalFilterModel = null
                if (this.onlyPendingEvents) {
                    externalFilterModel = {
                        'confirmationDate': GridUtils.getTextFilterModel('equals', null)
                    }
                }
                this.gridApi.deselectAll()
                this.gridApi.setServerSideDatasource(new PersonnelEventsForEventManagersDatasource(this.laboratoryId, externalFilterModel))
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        show() {
            this.$refs.eventManagementModal.show()
        },
        hide() {
            this.onlyPendingEvents = false
            this.selectedRows = []
            this.$refs.eventManagementModal.hide()
        },
        async confirmUnconfirmedPersonnelEvent(events) {
            const unconfirmedEvents = events.filter(calendarEvent => !calendarEvent.confirmationDate)
            let batchError = false
            this.updateLoading(true)
            for (let event of unconfirmedEvents) {
                const request = {
                    personnelEventId: event.id,
                    personnelId: event.personnel.id
                }
                const [error] = await this.toCall(PersonnelEventsForEventManagersServices.confirmUnconfirmedPersonnelEvent(request.personnelId, request.personnelEventId, this.laboratoryId))
                if (error) {
                    this.showErrorModalGlobalFromErrorResponse(error)
                    batchError = true
                }
            }
            if (!batchError) {
                this.showToastSuccessGlobal()
            }
            this.reloadData()
            this.$emit('eventManagementUpdated')
            this.updateLoading(false)
        },
        async deletePersonnelEvent(personnelEvents) {
            const deleteConfirmation = await this.showConfirmationModalGlobal()
            if (deleteConfirmation) {
                let batchError = false
                this.updateLoading(true)
                for (let eventToDelete of personnelEvents) {
                    const personnelId = eventToDelete.personnelId ? eventToDelete.personnelId : eventToDelete.personnel.id
                    const personnelEventId = eventToDelete.personnelEventId ? eventToDelete.personnelEventId : eventToDelete.id
                    const [error] = await this.toCall(PersonnelEventsForEventManagersServices.deletePersonnelEvent(personnelId, personnelEventId, this.laboratoryId))
                    if (error) {
                        this.showErrorModalGlobalFromErrorResponse(error)
                        batchError = true
                    }
                }
                if (!batchError) {
                    this.showToastSuccessGlobal()
                }
                this.reloadData()
                this.$emit('eventManagementUpdated')
                this.updateLoading(false)
            }
        },
        async updatePersonnelEvent(event) {
            this.updateLoading(true)
            const request = {
                endingDate: event.endDateSelected,
                observations: event.observations,
                startingDate: event.startDateSelected,
                type: event.eventTypeSelected.code
            }
            const [error] = await this.toCall(PersonnelEventsForEventManagersServices.updatePersonnelEvent(request, event.userSelected.id, event.selectedEventId, this.laboratoryId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return
            }
            this.reloadData()
            this.showToastSuccessGlobal()
        },
        async createAndConfirmPersonnelEvent(event) {
            this.updateLoading(true)
            const request = {
                endingDate: DateUtils.formatDate(event.endDateSelected),
                observations: event.observations,
                startingDate: DateUtils.formatDate(event.startDateSelected),
                type: event.eventTypeSelected.code
            }
            const [error] = await this.toCall(PersonnelEventsForEventManagersServices.createAndConfirmPersonnelEvent(request, event.userSelected.id, this.laboratoryId))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return
            }
            this.reloadData()
            this.$emit('eventManagementUpdated')
            this.showToastSuccessGlobal()
        },
        canConfirm(selectedRows) {
            return selectedRows.some(calendarEvent => calendarEvent.confirmationDate === null)
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                const selectedRows = this.selectedRows.length ? this.selectedRows : [params?.node.data]
                return [
                    {
                        name: this.$t('component.calendar.update'),
                        cssClasses: selectedRows.length === 1 ? '' : ['not-show'],
                        action: () => this.$refs.modalEventCalendarWrapper.show([params?.node.data])
                    },
                    {
                        name: this.$t('component.calendar.delete'),
                        cssClasses: selectedRows.length ? '' : ['not-show'],
                        action: async () => {
                            await this.deletePersonnelEvent(selectedRows)
                        }
                    },
                    {
                        name: this.$t('component.calendar.validate'),
                        cssClasses: this.canConfirm(selectedRows) ? '' : ['not-show'],
                        action: () => this.confirmUnconfirmedPersonnelEvent(selectedRows)
                    }
                ]
            }
        }
    }
}
</script>

<style lang="scss">
    .event-management-modal .event-management-grid {
        height: calc(100vh - 360px);
    }
</style>
